import React, { useState, useEffect, useMemo } from 'react';
import { 
  Copy, Download, Check, FileText, Box, Shield, 
  Terminal, Layers, Code, Hash, X, Server, Command, Globe, 
  Settings, Eye
} from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '../ui/Button';
import toast from 'react-hot-toast';

// --- GENERATOR UTILITY (Unchanged) ---
const generateOutput = (format, secrets, namespace = 'default') => {
  if (!secrets || secrets.length === 0) return '';
  const timestamp = new Date().toISOString();

  switch (format) {
    case 'env': return secrets.map(s => `${s.key}="${s.value}"`).join('\n');
    case 'docker': return `version: "3.8"\nservices:\n  app:\n    image: your-app-image\n    environment:\n${secrets.map(s => `      - ${s.key}=${s.value}`).join('\n')}`;
    case 'k8s-secret': return `apiVersion: v1\nkind: Secret\nmetadata:\n  name: envdock-export\n  namespace: ${namespace}\ntype: Opaque\nstringData:\n${secrets.map(s => `  ${s.key}: "${s.value}"`).join('\n')}`;
    case 'k8s-config': return `apiVersion: v1\nkind: ConfigMap\nmetadata:\n  name: envdock-config\n  namespace: ${namespace}\ndata:\n${secrets.map(s => `  ${s.key}: "${s.value}"`).join('\n')}`;
    case 'json': const jsonObj = secrets.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {}); return JSON.stringify(jsonObj, null, 2);
    case 'shell': return `#!/bin/bash\n# Exported from EnvDock on ${timestamp}\n\n` + secrets.map(s => `export ${s.key}="${s.value}"`).join('\n');
    case 'powershell': return `# PowerShell Export\n# Run: .\\envdock-export.ps1\n\n` + secrets.map(s => `$env:${s.key} = "${s.value}"`).join('\n');
    case 'github': return `# GitHub Actions Workflow\n# Add to .github/workflows/main.yml\n\nenv:\n` + secrets.map(s => `  ${s.key}: \${{ secrets.${s.key} }}`).join('\n');
    case 'gitlab': return `# GitLab CI Variables\n# Add to .gitlab-ci.yml\n\nvariables:\n` + secrets.map(s => `  ${s.key}: "${s.value}"`).join('\n');
    case 'terraform': return `# Terraform .tfvars\n\n` + secrets.map(s => `${s.key.toLowerCase()} = "${s.value}"`).join('\n');
    case 'java': return `# Java Properties File\n# Created: ${timestamp}\n\n` + secrets.map(s => `${s.key.replace(/_/g, '.').toLowerCase()}=${s.value}`).join('\n');
    case 'php': return `<?php\n// PHP Constants Export\n\n` + secrets.map(s => `define('${s.key}', '${s.value}');`).join('\n') + `\n?>`;
    case 'vercel': return `# Vercel CLI Import\n# Run: vercel env pull .env.local\n\n` + secrets.map(s => `${s.key}=${s.value}`).join('\n');
    default: return '';
  }
};

const FORMATS = [
  { id: 'env', label: '.env File', icon: FileText, desc: 'Standard dotenv' },
  { id: 'json', label: 'JSON', icon: Code, desc: 'Key-value object' },
  { id: 'shell', label: 'Bash', icon: Terminal, desc: 'Export commands' },
  { id: 'powershell', label: 'PowerShell', icon: Command, desc: 'Windows PS env' },
  { id: 'docker', label: 'Docker', icon: Box, desc: 'Compose YAML' },
  { id: 'k8s-secret', label: 'K8s Secret', icon: Shield, desc: 'K8s StringData' },
  { id: 'k8s-config', label: 'ConfigMap', icon: Layers, desc: 'K8s Data' },
  { id: 'terraform', label: 'Terraform', icon: Globe, desc: '.tfvars format' },
  { id: 'github', label: 'GitHub', icon: Server, desc: 'Actions YAML' },
  { id: 'gitlab', label: 'GitLab', icon: Server, desc: 'CI Variables' },
  { id: 'java', label: 'Java', icon: Hash, desc: 'Properties' },
  { id: 'php', label: 'PHP', icon: Code, desc: 'Constants' },
];

export default function ExportModal({ isOpen, onClose, secrets = [], envName }) {
  const [selectedFormat, setSelectedFormat] = useState('env');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [includeSecrets, setIncludeSecrets] = useState(true); 
  const [namespace, setNamespace] = useState('default');
  const [copied, setCopied] = useState(false);
  const [mobileTab, setMobileTab] = useState('settings');

  useEffect(() => {
    if (isOpen && secrets.length > 0) {
      setSelectedKeys(secrets.map(s => s.key));
    }
  }, [isOpen, secrets]);

  const output = useMemo(() => {
    const filtered = secrets.filter(s => selectedKeys.includes(s.key));
    const processed = filtered.map(s => ({
      key: s.key,
      value: includeSecrets ? s.value : '********'
    }));
    return generateOutput(selectedFormat, processed, namespace);
  }, [selectedFormat, selectedKeys, includeSecrets, secrets, namespace]);

  const handleCopy = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    toast.success("Format copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    let ext = 'env';
    if (['k8s-secret', 'k8s-config', 'docker', 'github', 'gitlab'].includes(selectedFormat)) ext = 'yaml';
    if (selectedFormat === 'json') ext = 'json';
    if (selectedFormat === 'shell') ext = 'sh';
    if (selectedFormat === 'powershell') ext = 'ps1';
    if (selectedFormat === 'terraform') ext = 'tfvars';
    if (selectedFormat === 'java') ext = 'properties';
    if (selectedFormat === 'php') ext = 'php';
    
    a.download = `envdock-${envName}-${selectedFormat}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Download started");
  };

  const toggleKey = (key) => {
    setSelectedKeys(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  if (!isOpen) return null;

  return (
    // FIX 1: Z-Index boosted to 9999 to definitely beat the sidebar
    <div className="fixed inset-0 z-[90]  flex items-center justify-center p-0 md:p-4 bg-black/90 backdrop-blur-sm mt-0">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="w-full h-full md:h-auto md:max-h-[80vh] md:max-w-6xl bg-[#0a0a0a] border-0 md:border border-white/10 md:rounded-[2rem] shadow-2xl overflow-hidden flex flex-col md:flex-row md:mt-0 mt-[64px]"
      >
        
        {/* === HEADER === */}
        <div className="md:hidden flex items-center justify-between p-6 border-b border-white/5 bg-[#0a0a0a]">
            <div>
              <h2 className="text-lg font-black text-white uppercase tracking-tighter">Export Variables</h2>
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{envName}</p>
            </div>
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-full text-gray-400">
                <X size={20} />
            </button>
        </div>

        {/* === MOBILE TABS === */}
        <div className="md:hidden flex p-4 pb-0 gap-2">
            <button 
                onClick={() => setMobileTab('settings')}
                className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 border transition-all ${mobileTab === 'settings' ? 'bg-white text-black border-white' : 'bg-white/5 text-gray-500 border-transparent'}`}
            >
                <Settings size={14} /> Configure
            </button>
            <button 
                onClick={() => setMobileTab('preview')}
                className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 border transition-all ${mobileTab === 'preview' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white/5 text-gray-500 border-transparent'}`}
            >
                <Eye size={14} /> Preview
            </button>
        </div>

        {/* === LEFT COLUMN: CONFIGURATION === */}
        <div className={`
            w-full md:w-2/5 border-r border-white/5 flex flex-col bg-white/[0.01] overflow-hidden
            ${mobileTab === 'settings' ? 'flex' : 'hidden md:flex'}
        `}>
          <div className="hidden md:flex p-6 md:p-8 border-b border-white/5 justify-between items-center">
            <div>
              <h2 className="text-xl font-black text-white uppercase tracking-tighter">Export <span className="text-orange-500">Variables</span></h2>
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Target: {envName}</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-8  md:space-y-8 custom-scrollbar">
            
            {/* 1. Format Selection */}
            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">01. Export Format</label>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                {FORMATS.map(fmt => (
                  <button
                    key={fmt.id}
                    onClick={() => setSelectedFormat(fmt.id)}
                    className={`flex flex-col items-start p-3 rounded-xl md:rounded-2xl border transition-all duration-300 text-left relative overflow-hidden
                      ${selectedFormat === fmt.id 
                        ? 'bg-orange-500/10 border-orange-500 text-white' 
                        : 'bg-white/[0.02] border-white/5 text-gray-500 hover:border-white/20'
                      }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                        <fmt.icon size={14} className={selectedFormat === fmt.id ? 'text-orange-500' : 'text-gray-600'} />
                        <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest">{fmt.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* 2. Kubernetes Extras */}
            {selectedFormat.includes('k8s') && (
               <div className="space-y-2 animate-fadeIn">
                 <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">K8s Namespace</label>
                 <input 
                    type="text" 
                    value={namespace} 
                    onChange={(e) => setNamespace(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-xs text-white focus:border-orange-500 outline-none font-mono"
                 />
               </div>
            )}

            {/* 3. Variables Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">02. Select Variables</label>
                <div className="flex gap-3">
                   <button onClick={() => setSelectedKeys(secrets.map(s => s.key))} className="text-[9px] text-orange-500 hover:text-white transition-colors uppercase font-bold tracking-widest p-2">All</button>
                   <span className="text-white/10 py-2">|</span>
                   <button onClick={() => setSelectedKeys([])} className="text-[9px] text-gray-600 hover:text-white transition-colors uppercase font-bold tracking-widest p-2">None</button>
                </div>
              </div>
              
              <div className="max-h-60 md:max-h-48 overflow-y-auto space-y-1 pr-2 custom-scrollbar">
                {secrets.map(secret => (
                  <div key={secret.key} onClick={() => toggleKey(secret.key)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer group active:bg-white/10">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${selectedKeys.includes(secret.key) ? 'bg-orange-500 border-orange-500' : 'border-white/20'}`}>
                      {selectedKeys.includes(secret.key) && <Check size={10} className="text-black stroke-[4]" />}
                    </div>
                    <span className={`text-xs font-mono truncate ${selectedKeys.includes(secret.key) ? 'text-white' : 'text-gray-600'}`}>{secret.key}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="h-20 md:hidden" />
          </div>
        </div>

        {/* === RIGHT COLUMN: PREVIEW === */}
        <div className={`
            w-full md:w-3/5 bg-black/20 flex-col min-h-[400px] relative
            ${mobileTab === 'preview' ? 'flex h-full' : 'hidden md:flex'}
        `}>
           <div className="p-4 md:p-8 flex items-center justify-between border-b border-white/5 bg-[#0a0a0a] md:bg-transparent">
              <div className="flex items-center gap-3">
                 <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      id="reveal" 
                      checked={includeSecrets} 
                      onChange={(e) => setIncludeSecrets(e.target.checked)}
                      className="w-5 h-5 md:w-4 md:h-4 rounded border-gray-600 bg-transparent text-orange-500 focus:ring-offset-black focus:ring-orange-500"
                    />
                    <label htmlFor="reveal" className={`text-[10px] font-black uppercase tracking-widest cursor-pointer select-none ${includeSecrets ? 'text-red-500 animate-pulse' : 'text-gray-500'}`}>
                        {includeSecrets ? 'Values Exposed' : 'Redact Values'}
                    </label>
                 </div>
              </div>
              
              <button onClick={onClose} className="hidden md:flex text-gray-500 hover:text-white transition-colors">
                  <X size={24} />
              </button>
           </div>

           <div className="flex-1 relative group overflow-hidden bg-[#050505] flex flex-col">
              <div className="absolute top-4 right-4 z-10 md:opacity-0 group-hover:opacity-100 transition-opacity">
                 <button onClick={handleCopy} className="p-3 md:p-2 bg-white/10 hover:bg-white/20 rounded-xl md:rounded-lg text-white backdrop-blur-md transition-all border border-white/5">
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                 </button>
              </div>
              
              {/* FIX 2: Added 'whitespace-pre-wrap' and 'break-all' to force text wrapping */}
              <pre className="flex-1 w-full h-full p-6 md:p-8 overflow-auto text-[11px] md:text-xs font-mono leading-relaxed text-gray-300 selection:bg-orange-500/30 selection:text-white custom-scrollbar whitespace-pre-wrap break-all">
                 <code>{output}</code>
              </pre>
              <div className="h-24 md:hidden" />
           </div>

           <div className="absolute bottom-0 left-0 z-50 right-0 p-4 md:p-6 border-t border-white/5 bg-[#0a0a0a]  md:bg-orange-500 flex items-center justify-between gap-4 z-20">
              <div className="hidden md:block">
                 <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">
                    {selectedKeys.length} Variables
                 </p>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                 <Button onClick={onClose} variant="outline" className="hidden md:flex flex-1 md:flex-none">Cancel</Button>
                 <Button onClick={handleDownload} variant="primary" className="flex-1 md:flex-none px-6 h-12 md:h-10 text-center justify-center">
                    <Download size={16} className="mr-2" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Download</span>
                 </Button>
              </div>
           </div>
        </div>

      </motion.div>
    </div>
  );
}